import type { Attachment, Coding, DocumentReference } from '@medplum/fhirtypes'
import {
  ATTACHMENT_CONVERSION_PENDING_TAG_CODE,
  ATTACHMENT_CONVERSION_TAG_SYSTEM,
} from '#src/codeSystems.ts'
import { monitorResource, type PatchOperation } from '#src/monitorResource.ts'
import { ATTACHMENT_CONVERSION_EXTENSION } from '#src/structureDefinitions.ts'
import { matchTag } from '#src/tags.ts'

export { ATTACHMENT_CONVERSION_EXTENSION } from '#src/structureDefinitions.ts'

export type AttachmentConversionStatus = 'pending' | 'failed'

export interface AttachmentConversionState {
  status: AttachmentConversionStatus
  startedAt: string
}

export function getAttachmentConversionState(
  attachment: Attachment
): AttachmentConversionState | undefined {
  const ext = attachment.extension?.find(
    (e) => e.url === ATTACHMENT_CONVERSION_EXTENSION
  )
  if (!ext) {
    return undefined
  }

  const status = ext.extension?.find((e) => e.url === 'status')?.valueCode as
    | AttachmentConversionStatus
    | undefined
  const startedAt = ext.extension?.find(
    (e) => e.url === 'startedAt'
  )?.valueDateTime

  if (!status || !startedAt) {
    return undefined
  }
  return { status, startedAt }
}

export function getAttachmentConversionStatus(
  attachment: Attachment
): AttachmentConversionStatus | undefined {
  return getAttachmentConversionState(attachment)?.status
}

export function isAttachmentConversionPending(attachment: Attachment): boolean {
  return getAttachmentConversionStatus(attachment) === 'pending'
}

export function isAttachmentConversionFailed(attachment: Attachment): boolean {
  return getAttachmentConversionStatus(attachment) === 'failed'
}

export function isAttachmentReadyForConsumers(attachment: Attachment): boolean {
  return getAttachmentConversionStatus(attachment) === undefined
}

export function setAttachmentConversionState(
  attachment: Attachment,
  state: AttachmentConversionState
): Attachment {
  const otherExtensions =
    attachment.extension?.filter(
      (e) => e.url !== ATTACHMENT_CONVERSION_EXTENSION
    ) ?? []
  return {
    ...attachment,
    extension: [
      ...otherExtensions,
      {
        url: ATTACHMENT_CONVERSION_EXTENSION,
        extension: [
          { url: 'status', valueCode: state.status },
          { url: 'startedAt', valueDateTime: state.startedAt },
        ],
      },
    ],
  }
}

export function clearAttachmentConversionState(
  attachment: Attachment
): Attachment {
  if (!attachment.extension) {
    return attachment
  }
  const remaining = attachment.extension.filter(
    (e) => e.url !== ATTACHMENT_CONVERSION_EXTENSION
  )
  if (remaining.length === attachment.extension.length) {
    return attachment
  }
  return {
    ...attachment,
    extension: remaining.length > 0 ? remaining : undefined,
  }
}

export const ATTACHMENT_CONVERSION_PENDING_TAG: Coding = {
  system: ATTACHMENT_CONVERSION_TAG_SYSTEM,
  code: ATTACHMENT_CONVERSION_PENDING_TAG_CODE,
}

export function hasPendingAttachmentConversion(
  content: DocumentReference['content'] | undefined
): boolean {
  return (content ?? []).some(
    (item) => item.attachment && isAttachmentConversionPending(item.attachment)
  )
}

export function hasAttachmentConversionPendingTag(
  resource: DocumentReference
): boolean {
  return (resource.meta?.tag ?? []).some((tag) =>
    matchTag(tag, ATTACHMENT_CONVERSION_PENDING_TAG)
  )
}

// Patch operations that make `meta.tag` carry the pending-conversion tag exactly
// when `content` holds a pending attachment. Empty when the tag already agrees,
// so a write that leaves the document consistent costs nothing and callers
// cannot loop on their own writes.
//
// `content` is the attachment list that will be in place once the caller's own
// write lands, which lets one patch change the attachments and the tag together.
// It defaults to the stored content. Only `meta.tag` is read from `resource`.
export function attachmentConversionTagOperations(
  resource: DocumentReference,
  content: DocumentReference['content'] | undefined = resource.content
): PatchOperation<'DocumentReference'>[] {
  const tags = resource.meta?.tag ?? []
  const tagged = hasAttachmentConversionPendingTag(resource)
  const shouldBeTagged = hasPendingAttachmentConversion(content)
  if (tagged === shouldBeTagged) {
    return []
  }

  const nextTags = shouldBeTagged
    ? [...tags, ATTACHMENT_CONVERSION_PENDING_TAG]
    : tags.filter((tag) => !matchTag(tag, ATTACHMENT_CONVERSION_PENDING_TAG))
  const monitored = monitorResource<'DocumentReference'>(resource)
  if (monitored.proxy.meta) {
    monitored.proxy.meta.tag = nextTags
  } else {
    monitored.proxy.meta = { tag: nextTags }
  }
  return monitored.patches
}
